import React, { useState, useEffect, useRef, useCallback } from 'react';
import SimuladorJugada from './SimuladorJugada';
import TablaSuenos from './TablaSuenos';
import { TABLA_SUENOS } from './mecanicasJuegos';
import './NumeroSuerte.css';

const PUB = process.env.PUBLIC_URL;
const ALTO_DIGITO = 76; // px, alto de cada dígito en el tambor
const VUELTAS = 3;      // vueltas completas antes de frenar

/* ─────────────────── DECISIÓN ABIERTA · el giro automático ────────────────
   `true`  = la máquina arranca a girar sola al entrar a su pestaña.
   `false` = queda quieta y gira cuando el visitante tira de la palanca.

   Está en una sola constante a propósito: prenderlo o apagarlo es esta línea
   y nada más. La decisión es de Ricardo, no del implementador.

   A favor de dejarlo en `true`: el giro es la recompensa de la pieza y verlo
   pasar solo la primera vez explica qué hace sin una línea de instrucciones.
   En contra: la guía de diseño del vertical lo clasifica como "animación
   agresiva" —movimiento de casino que arranca sin que el visitante lo pida—
   y ahora la máquina ya no es lo primero que se ve, así que el argumento
   "hay que explicar qué es esto" pesa menos que antes.

   Se deja como estaba (`true`) para no cambiar dos cosas a la vez.          */
const GIRO_AUTOMATICO = true;

/* Las tres secciones del modal. La máquina se queda —decisión explícita de
   Ricardo— y ahora tiene su lugar propio en vez de ser todo el contenido. */
const SECCIONES = [
  {
    id: 'jugada',
    tab: 'Tu jugada',
    kicker: 'Simulador',
    titulo: 'Armá tu jugada',
    bajada: 'Elegí un juego y marcá el cartón como si estuvieras en la agencia. Es para entender cómo se juega cada uno.',
  },
  {
    id: 'suenos',
    tab: 'Los sueños',
    kicker: 'Quiniela',
    titulo: 'La tabla de los sueños',
    bajada: 'Los cien números con su significado. Buscá por sueño o por número y llevátelo al cartón.',
  },
  {
    id: 'maquina',
    tab: 'La máquina',
    kicker: 'Para divertirse',
    titulo: 'Tu número de la suerte',
    bajada: 'Girá la máquina y llevate un número de cuatro cifras, con su significado de la tabla de los sueños.',
  },
];

const movimientoReducido = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ─────────────────────────── La máquina ───────────────────────────
   Misma mecánica de siempre: 4 tambores manejados por refs (reset arriba
   sin transición + giro completo con transición), así el giro es total
   aunque se repita el dígito.                                              */
function Maquina({ girarAlAbrir, onJugar }) {
  const [girando, setGirando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const stripRefs = useRef([]);
  const timerRef = useRef(null);

  const posicionar = (strip, digito, conTransicion, i) => {
    strip.style.transition = conTransicion
      ? `transform ${1.3 + i * 0.4}s cubic-bezier(.15,.85,.35,1.02)`
      : 'none';
    strip.style.transform = `translateY(-${(VUELTAS * 10 + digito) * ALTO_DIGITO}px)`;
  };

  const girar = useCallback(() => {
    setGirando((yaGirando) => {
      if (yaGirando) return yaGirando;

      const nuevos = Array.from({ length: 4 }, () => Math.floor(Math.random() * 10));
      setResultado(null);

      if (movimientoReducido()) {
        stripRefs.current.forEach((s, i) => { if (s) posicionar(s, nuevos[i], false, i); });
        setResultado(nuevos);
        return false;
      }

      stripRefs.current.forEach((s) => {
        if (s) { s.style.transition = 'none'; s.style.transform = 'translateY(0)'; }
      });
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          stripRefs.current.forEach((s, i) => { if (s) posicionar(s, nuevos[i], true, i); });
        });
      });

      // El último tambor frena a los ~2.5s; después mostramos el significado
      timerRef.current = setTimeout(() => {
        setGirando(false);
        setResultado(nuevos);
      }, 2700);

      return true;
    });
  }, []);

  // Posición inicial 7777 y, si vino de tirar la palanca, arranca girando.
  useEffect(() => {
    stripRefs.current.forEach((s, i) => { if (s) posicionar(s, 7, false, i); });
    if (girarAlAbrir) {
      const id = setTimeout(girar, 260);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [girarAlAbrir, girar]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const numero = resultado ? resultado.join('') : null;
  const dosUltimos = resultado ? (resultado[2] * 10 + resultado[3]) : null;

  return (
    <div className="hc-slot ns-slot">
      <div className={`hc-maquina-real${girando ? ' girando' : ''}`}>
        <img
          className="hc-maq-img"
          src={`${PUB}/img/juegos/maquina.png`}
          alt="Máquina tragamonedas de la suerte"
          loading="lazy"
        />
        <div className="hc-maq-placa" aria-hidden="true">Máquina de la suerte</div>
        <div className="hc-maq-ventana">
          <div className="hc-reels" aria-label={numero ? `Número generado: ${numero}` : 'Máquina de números'}>
            {[0, 1, 2, 3].map((i) => (
              <div className="hc-reel" key={i}>
                <div className="hc-reel-strip" ref={(el) => { stripRefs.current[i] = el; }}>
                  {Array.from({ length: VUELTAS * 10 + 10 }, (_, k) => (
                    <span key={k} className="hc-reel-digit">{k % 10}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          className="hc-maq-hotspot"
          onClick={girar}
          disabled={girando}
          aria-label="Tirar de la palanca"
        />
      </div>

      <button className="hc-btn hc-btn-gold hc-slot-btn" onClick={girar} disabled={girando}>
        {girando ? 'Girando…' : 'Girar la suerte'}
      </button>

      <p className={`hc-slot-meaning${resultado ? ' visible' : ''}`} aria-live="polite">
        {resultado
          ? <>Salió el <b>{numero}</b> — y el {String(dosUltimos).padStart(2, '0')} es <b>{TABLA_SUENOS[dosUltimos]}</b> en la tabla de los sueños</>
          : 'La suerte espera tu giro'}
      </p>

      {/* El número que tira la máquina son cuatro cifras: exactamente una
          jugada de quiniela. Este botón es lo que le faltaba al cospel —
          hasta ahora entregaba un número suelto, sin relación con ningún
          juego, y ahí se perdía toda la capacidad de explicar. */}
      {numero && onJugar && (
        <button type="button" className="ns-llevar" onClick={() => onJugar(numero)}>
          Armar la jugada con el {numero}
        </button>
      )}

      <small className="hc-slot-disclaimer">Solo para divertirse. Jugá con responsabilidad, +18.</small>
    </div>
  );
}

/* ───────────────────────── El cospel ─────────────────────────────
   Ficha con espesor real: dos caras separadas por COS_ESPESOR y un canto
   hecho de segmentos planos tangentes al cilindro. Las rayas alternadas
   del canto son las de una ficha de verdad, y hacen legible el giro.    */
const COS_RADIO = 38;      // px
const COS_ESPESOR = 13;    // px
const COS_SEGMENTOS = 44;  // cuantos más, más redondo el canto

function DisparadorCospel({ onAbrir, oculto }) {
  const arco = (2 * Math.PI * COS_RADIO) / COS_SEGMENTOS;

  return (
    <button
      type="button"
      className={`ns-trigger ns-cospel${oculto ? ' oculto' : ''}`}
      onClick={onAbrir}
      aria-label="Abrir la máquina de la suerte"
    >
      <span className="ns-cos-piso" aria-hidden="true" />
      <span className="ns-cos-escena" aria-hidden="true">
        <span className="ns-cos-ficha">
          <span className="ns-cos-cara ns-cos-frente"><b>777</b></span>
          <span className="ns-cos-cara ns-cos-dorso"><b>777</b></span>

          {Array.from({ length: COS_SEGMENTOS }, (_, i) => (
            <span
              key={i}
              className={`ns-cos-canto${i % 6 < 2 ? ' marca' : ''}`}
              style={{
                width: `${COS_ESPESOR}px`,
                height: `${arco + 1.4}px`,
                marginTop: `${-(arco + 1.4) / 2}px`,
                marginLeft: `${-COS_ESPESOR / 2}px`,
                transform: `rotateZ(${i * (360 / COS_SEGMENTOS)}deg) translateX(${COS_RADIO}px) rotateY(90deg)`,
              }}
            />
          ))}
        </span>
      </span>
      <span className="ns-cos-rotulo" aria-hidden="true">Probá tu suerte</span>
    </button>
  );
}

/* ─────────────────────────── El modal ──────────────────────────────
   El armazón (foco atrapado, Esc, clic afuera, scroll del body bloqueado)
   es el que ya estaba y no se reescribió: solo se le colgó adentro una
   fila de pestañas y las tres piezas.                                    */
function ModalSuerte({ onCerrar, seccionInicial, juegoInicial }) {
  const panelRef = useRef(null);
  const cerrarRef = useRef(null);
  const cuerpoRef = useRef(null);

  const [seccion, setSeccion] = useState(seccionInicial);
  const [juegoSim, setJuegoSim] = useState(juegoInicial);
  // La semilla viaja de la tabla de los sueños al cartón de quiniela. Es un
  // objeto nuevo en cada envío a propósito: así el efecto que la aplica se
  // dispara aunque el número sea el mismo dos veces seguidas.
  const [semilla, setSemilla] = useState(null);

  const activa = SECCIONES.find((s) => s.id === seccion) || SECCIONES[0];

  const irA = useCallback((id) => {
    setSeccion(id);
    if (cuerpoRef.current) cuerpoRef.current.scrollTop = 0;
    if (panelRef.current) panelRef.current.scrollTop = 0;
  }, []);

  // El puente entre las tres piezas: un sueño o un número de la máquina se
  // convierten en una jugada de Tómbola con su forma correcta. Es justo lo
  // que hoy no pasa — el cospel entrega un número suelto, sin juego.
  const jugarNumero = useCallback((texto, cifras) => {
    setJuegoSim('tombola');
    setSemilla({ texto, cifras, en: Date.now() });
    irA('jugada');
  }, [irA]);

  // Flechas para moverse entre pestañas, como pide el patrón de tabs.
  const alTeclearTabs = (e) => {
    const i = SECCIONES.findIndex((s) => s.id === seccion);
    let destino = null;
    if (e.key === 'ArrowRight') destino = (i + 1) % SECCIONES.length;
    if (e.key === 'ArrowLeft') destino = (i - 1 + SECCIONES.length) % SECCIONES.length;
    if (e.key === 'Home') destino = 0;
    if (e.key === 'End') destino = SECCIONES.length - 1;
    if (destino === null) return;
    e.preventDefault();
    irA(SECCIONES[destino].id);
    const botones = e.currentTarget.querySelectorAll('button');
    if (botones[destino]) botones[destino].focus();
  };

  useEffect(() => {
    cerrarRef.current?.focus();

    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const alTeclear = (e) => {
      if (e.key === 'Escape') { onCerrar(); return; }
      if (e.key !== 'Tab') return;

      const foco = panelRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (!foco || !foco.length) return;

      const primero = foco[0];
      const ultimo = foco[foco.length - 1];
      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault(); ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault(); primero.focus();
      }
    };

    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      document.body.style.overflow = previo;
    };
  }, [onCerrar]);

  return (
    <div className="ns-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }}>
      <div className="ns-panel" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="ns-titulo">
        <div className="ns-panel-head">
          <div>
            <p className="ns-kicker">{activa.kicker}</p>
            <h2 id="ns-titulo">{activa.titulo}</h2>
          </div>
          <button type="button" className="ns-cerrar" onClick={onCerrar} ref={cerrarRef} aria-label="Cerrar">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" />
            </svg>
          </button>
        </div>

        <div
          className="ns-tabs"
          role="tablist"
          aria-label="Qué querés hacer"
          onKeyDown={alTeclearTabs}
        >
          {SECCIONES.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              id={`ns-tab-${s.id}`}
              aria-selected={s.id === seccion}
              aria-controls={`ns-panel-${s.id}`}
              className={`ns-tab${s.id === seccion ? ' activa' : ''}`}
              onClick={() => irA(s.id)}
            >
              {s.tab}
            </button>
          ))}
        </div>

        <p className="ns-bajada">{activa.bajada}</p>

        <div
          className="ns-cuerpo"
          ref={cuerpoRef}
          role="tabpanel"
          id={`ns-panel-${activa.id}`}
          aria-labelledby={`ns-tab-${activa.id}`}
          tabIndex={-1}
        >
          {activa.id === 'jugada' && (
            <SimuladorJugada
              clave={juegoSim}
              onClave={setJuegoSim}
              semilla={semilla}
              onVerSuenos={() => irA('suenos')}
            />
          )}
          {activa.id === 'suenos' && (
            <TablaSuenos onJugar={(n) => jugarNumero(String(n).padStart(2, '0'), 2)} />
          )}
          {activa.id === 'maquina' && (
            <Maquina
              girarAlAbrir={GIRO_AUTOMATICO}
              onJugar={(texto) => jugarNumero(texto, 4)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── Público ──────────────────────────────
   El disparador sigue siendo el cospel flotante. Cuando P8 lo saque de
   flotante y lo pase a contenido dentro de la sección de extractos, lo que
   cambia es SOLO el disparador: `ModalSuerte` ya recibe en qué sección y en
   qué juego abrir, así que las ocho tarjetas de la grilla de juegos van a
   poder abrirlo cada una en el suyo — que es el destino que hoy les falta
   (`#como-se-juega` no existe, P5).                                       */
// Bloque de invitación, para montar DENTRO del contenido de la página.
//
// Reemplaza al disparador flotante. Un widget fijo en la esquina inferior
// derecha es la firma visual exacta de un chat o de un aviso, y eso dispara
// la ceguera al banner: el visitante lo filtra ANTES de leerlo (evidencia
// nivel A, consistente desde 1997). La contracara está medida: lo que se ve
// como contenido —misma tipografía, misma grilla— recibe MÁS atención que lo
// que se ve como aviso.
//
// Y había un motivo que no admitía discusión: flotando, el cospel tapaba en
// móvil la leyenda "EL JUGAR COMPULSIVAMENTE ES PERJUDICIAL PARA LA SALUD",
// que es obligatoria por norma.
function BloqueInvitacion({ onAbrir }) {
  return (
    <div className="ns-invita">
      <div className="ns-invita-texto">
        <p className="ns-invita-kicker">Antes de ir a la agencia</p>
        <h3>Armá tu jugada acá</h3>
        <p className="ns-invita-bajada">
          Elegí tus números en el cartón de cada juego y mirá cómo queda la
          jugada. Buscá qué número le toca a lo que soñaste, o dejá que la
          máquina elija por vos.
        </p>
        <button type="button" className="ns-invita-btn" onClick={onAbrir}>
          Probar sin compromiso <span aria-hidden="true">→</span>
        </button>
        <p className="ns-invita-nota">
          Es para practicar: no es una jugada real ni se registra en ningún lado.
        </p>
      </div>
      <div className="ns-invita-ficha" aria-hidden="true">
        <span className="ns-invita-cospel"><b>777</b></span>
      </div>
    </div>
  );
}

export default function NumeroSuerte({
  seccionInicial = 'jugada',
  juegoInicial = 'tombola',
}) {
  const [abierto, setAbierto] = useState(false);
  const [desplazado, setDesplazado] = useState(0);
  const disparadorRef = useRef(null);
  const cospelRef = useRef(null);

  const [juegoPedido, setJuegoPedido] = useState(null);

  const abrir = () => setAbierto(true);

  // El carrusel del hero abre el simulador en el juego de su lámina. Van por
  // evento y no por props porque viven en secciones distintas de la página y
  // no se conocen entre sí.
  useEffect(() => {
    const alPedir = (e) => {
      const juego = e.detail?.juego;
      if (juego) setJuegoPedido(juego);
      setAbierto(true);
    };
    window.addEventListener('inpro:abrir-simulador', alPedir);
    return () => window.removeEventListener('inpro:abrir-simulador', alPedir);
  }, []);
  const cerrar = useCallback(() => {
    setAbierto(false);
    setJuegoPedido(null);
    disparadorRef.current?.querySelector('button')?.focus();
  }, []);

  // El cospel NO desaparece nunca: si una leyenda de juego responsable le queda
  // debajo, se corre hacia arriba lo justo para no taparla.
  //
  // El problema es real y bloqueaba la publicación: la ficha es `position:fixed`
  // abajo a la derecha, que es exactamente donde cae la franja normada del
  // carrusel en móvil. Verificado a 390 y a 360 px, tapaba "ES PERJUDICIAL PARA
  // LA SALUD" y parte de la línea de ayuda.
  //
  // Esconderlo era la solución fácil y se descartó: el cospel es el gancho y
  // tiene que estar siempre a mano. Correrlo cumple las dos cosas.
  //
  // Se mide el solapamiento real en vez de usar un umbral de scroll fijo: si
  // mañana la franja cambia de alto o aparece en otro lado, esto sigue andando.
  useEffect(() => {
    // ⚠️ Las leyendas se consultan en CADA cálculo, no una vez al montar.
    // La franja del carrusel no existe cuando este componente monta: aparece
    // recién cuando contesta Firestore. Capturarla al inicio es la trampa 1 de
    // este repo —lo que llega después de una carga asíncrona queda fuera del
    // alcance de lo que se registró al montar— y acá dejaba el desplazamiento
    // siempre en cero, o sea el cospel tapando la leyenda igual que antes.
    // Se calcula derecho en el handler, sin requestAnimationFrame: rAF no
    // dispara con la pestaña en segundo plano, y eso volvía imposible verificar
    // esto de verdad. Son dos o tres getBoundingClientRect por evento, con el
    // listener en passive: barato.
    let ultimoY = -1;
    const recalcular = () => {
      const ficha = cospelRef.current?.querySelector('.ns-cospel');
      if (!ficha) return;

      // Se mide con el desplazamiento en cero para no realimentarse.
      const previo = ficha.style.transform;
      ficha.style.transform = 'none';
      const c = ficha.getBoundingClientRect();
      ficha.style.transform = previo;

      let subir = 0;
      document.querySelectorAll('.hc-jr').forEach((l) => {
        const r = l.getBoundingClientRect();
        const seCruzan = r.top < c.bottom && r.bottom > c.top
          && r.left < c.right && r.right > c.left;
        if (seCruzan) subir = Math.max(subir, c.bottom - r.top + 16);
      });
      setDesplazado(subir);
    };

    const alScrollear = () => {
      // Nada que hacer si el scroll no se movió lo suficiente como para cambiar
      // el solapamiento.
      const y = window.scrollY;
      if (Math.abs(y - ultimoY) < 4) return;
      ultimoY = y;
      recalcular();
    };

    recalcular();
    window.addEventListener('scroll', alScrollear, { passive: true });
    window.addEventListener('resize', alScrollear);
    return () => {
      window.removeEventListener('scroll', alScrollear);
      window.removeEventListener('resize', alScrollear);
    };
  }, []);

  return (
    <>
      <div ref={disparadorRef}>
        <BloqueInvitacion onAbrir={abrir} />
      </div>

      {/* El cospel vuelve por pedido explícito: es el gancho que engancha.
          Convive con el bloque de contenido — el bloque es el que se lee, el
          cospel es el que está siempre a mano. Solo se oculta con el modal
          abierto; si una leyenda le queda debajo, se corre, no desaparece. */}
      <div ref={cospelRef} style={{ '--ns-subir': `${desplazado}px` }}>
        <DisparadorCospel onAbrir={abrir} oculto={abierto} />
      </div>
      {abierto && (
        <ModalSuerte
          onCerrar={cerrar}
          seccionInicial={juegoPedido ? 'jugada' : seccionInicial}
          juegoInicial={juegoPedido || juegoInicial}
        />
      )}
    </>
  );
}
